import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Grid,
  IconButton,
  Collapse
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import FilterListIcon from "@material-ui/icons/FilterList";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import api from "../../services/api";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    width: '100%',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  filterTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 500,
  },
  filterGrid: {
    width: '100%',
  },
  filterField: {
    width: '100%',
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },
  tagsField: {
    width: '100%',
    '& .MuiAutocomplete-root': {
      width: '100%',
    },
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'wrap',
      minHeight: '40px',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  connectionField: {
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  chip: {
    backgroundColor: (props) => props?.color || "#eee",
    textShadow: "1px 1px 1px #000",
    color: "white",
    height: "20px",
    fontSize: "11px",
    maxWidth: "100px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
}));

export function ContactsFilter({ onFiltered, onClearFilters }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { whatsApps, loading: whatsAppsLoading } = useWhatsApps();
  const { user } = React.useContext(AuthContext);
  
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState("");
  const [loading, setLoading] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Verificar se o usuário pode alterar a conexão
  const canChangeConnection = () => {
    // Qualquer usuário (admin ou comum) só pode alterar se tiver permissão
    return user?.allowViewOtherConnections === "enabled";
  };

  useEffect(() => {
    loadTags();
  }, []);

  // Definir conexão padrão do usuário quando não tem permissão para alterar
  useEffect(() => {
    if (!canChangeConnection() && user?.whatsappId && !selectedConnection) {
      setSelectedConnection(user.whatsappId);
    }
  }, [user, selectedConnection]);

  // Reagir às mudanças de permissão em tempo real
  useEffect(() => {
    if (user?.allowViewOtherConnections === "disabled" && user?.whatsappId) {
      // Se a permissão foi desabilitada, forçar a conexão padrão do usuário
      if (selectedConnection !== user.whatsappId) {
        console.log("Permissão desabilitada - forçando conexão padrão:", user.whatsappId);
        setSelectedConnection(user.whatsappId);
        
        // Aplicar filtros automaticamente com a nova conexão
        setTimeout(() => {
          const filters = {
            tags: selectedTags,
            connection: user.whatsappId
          };
          onFiltered(filters);
        }, 100);
      }
    }
  }, [user?.allowViewOtherConnections, user?.whatsappId, selectedConnection, selectedTags, onFiltered]);

  const loadTags = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/tags/list`);
      setTags(data);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar tags:', err);
      toastError(err);
      setLoading(false);
    }
  };

  const handleTagsChange = (event, newValue) => {
    setSelectedTags(newValue);
  };

  const handleConnectionChange = (event) => {
    setSelectedConnection(event.target.value);
  };

  const handleApplyFilters = () => {
    const filters = {
      tags: selectedTags,
      connection: selectedConnection
    };
    onFiltered(filters);
  };

  const handleClearFilters = () => {
    setSelectedTags([]);
    setSelectedConnection("");
    onClearFilters();
  };

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Filtrar conexões baseado na permissão do usuário
  const getFilteredWhatsApps = () => {
    // Se não pode ver outras conexões, só mostra a conexão atribuída ao usuário
    if (user?.allowViewOtherConnections === "disabled") {
      return whatsApps.filter(whatsapp => whatsapp.id === user.whatsappId);
    }
    
    // Se pode ver outras conexões, mostra todas
    return whatsApps;
  };

  return (
    <Box className={classes.filterContainer}>
      {/* Header com botão de expandir/minimizar para mobile */}
      {isMobile && (
        <Box className={classes.filterHeader}>
          <Box className={classes.filterTitle}>
            <FilterListIcon />
            Filtros
          </Box>
          <IconButton onClick={toggleFilters} size="small">
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      )}

      {/* Conteúdo dos filtros */}
      <Collapse in={!isMobile || filtersExpanded}>
        <Grid container spacing={2} className={classes.filterGrid}>
        {/* Campo de Tags */}
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            multiple
            className={classes.tagsField}
            options={tags || []}
            value={selectedTags || []}
            onChange={handleTagsChange}
            getOptionLabel={(option) => option?.name || ""}
            getOptionSelected={(option, value) => {
              return (
                option?.id === value?.id ||
                (option?.name && value?.name && option.name.toLowerCase() === value.name.toLowerCase())
              );
            }}
            loading={loading}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  className={classes.chip}
                  style={{ backgroundColor: option?.color || "#eee" }}
                  label={option?.name || ""}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Selecione as tags"
                label="Tags"
                size="small"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  style: {
                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                  }
                }}
                InputLabelProps={{
                  style: {
                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Campo de Conexão - sempre mostra, mas pode estar desabilitado */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl 
            variant="outlined" 
            className={classes.connectionField} 
            size="small"
            fullWidth
          >
            <InputLabel style={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
              Conexão
            </InputLabel>
            <Select
              value={selectedConnection}
              onChange={handleConnectionChange}
              label="Conexão"
              disabled={whatsAppsLoading || !canChangeConnection()}
              style={{ 
                fontSize: isMobile ? '0.8rem' : '0.875rem'
              }}
            >
              <MenuItem value="" style={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                <em>Todas as conexões</em>
              </MenuItem>
              {getFilteredWhatsApps().map((whatsapp) => (
                <MenuItem 
                  key={whatsapp.id} 
                  value={whatsapp.id} 
                  style={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                >
                  {whatsapp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Botões de ação */}
        <Grid item xs={12} sm={12} md={4}>
          <Box className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              disabled={loading || whatsAppsLoading}
              size="small"
              fullWidth={isMobile}
              style={{ 
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                minHeight: '40px'
              }}
            >
              Aplicar
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
              disabled={loading || whatsAppsLoading}
              size="small"
              fullWidth={isMobile}
              style={{ 
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                minHeight: '40px'
              }}
            >
              Limpar
            </Button>
          </Box>
        </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
}
